/**
 * Focus Page
 *
 * Shows tasks that are currently in focus - the user's active working set.
 * Includes focus topics (parent paths) and soft limit warnings.
 */

import { useState, useMemo } from 'react';
import { Target, FolderTree, AlertCircle, CheckCircle2, Plus } from 'lucide-react';
import { useTasksFocus, useTaskMutations } from '@/hooks/use-tasks';
import { TaskList, TaskEditModal } from '@/components/tasks';
import { Card, CardContent, Button } from '@/components/ui';
import type { Task, UpdateTaskInput } from '@/types';

// Default soft limit for focus tasks (can be made configurable later)
const DEFAULT_FOCUS_LIMIT = 5;

export function FocusPage() {
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());
  
  // Get focus limit from localStorage (or default)
  const focusLimit = useMemo(() => {
    const stored = localStorage.getItem('lifeguide_focus_limit');
    return stored ? parseInt(stored, 10) : DEFAULT_FOCUS_LIMIT;
  }, []);
  
  // Fetch focused tasks
  const focusQuery = useTasksFocus();
  const { updateTask, completeTask, reopenTask, unfocusTask, dismissTask, convertToReference } = useTaskMutations();
  
  const tasks = focusQuery.data?.tasks ?? [];
  const focusTopics = focusQuery.data?.focusTopics ?? {};
  const isLoading = focusQuery.isLoading;
  const focusCount = tasks.length;
  const isOverLimit = focusCount > focusLimit;
  
  // Sort topics by task count
  const sortedTopics = useMemo(() => {
    return Object.entries(focusTopics)
      .sort(([, a], [, b]) => b - a)
      .map(([topic, count]) => ({ topic, count }));
  }, [focusTopics]);
  
  // Handle edit task
  const handleEditTask = (task: Task) => {
    setEditingTask(task);
  };

  const handleSaveTask = (taskId: string, updates: UpdateTaskInput) => {
    updateTask.mutate(
      { taskId, input: updates },
      {
        onSuccess: () => {
          setEditingTask(null);
        },
      }
    );
  };

  // Handle complete/reopen task
  const handleCompleteTask = (taskId: string) => {
    completeTask.mutate(taskId);
  };

  const handleReopenTask = (taskId: string) => {
    reopenTask.mutate(taskId);
  };

  const handleDismissTask = (taskId: string) => {
    dismissTask.mutate(taskId);
  };

  const handleConvertToReference = (taskId: string) => {
    convertToReference.mutate(taskId);
  };
  
  // Handle unfocus
  const handleUnfocus = (taskId: string) => {
    unfocusTask.mutate(taskId);
  };
  
  // Toggle topic expansion
  const toggleTopic = (topic: string) => {
    const newExpanded = new Set(expandedTopics);
    if (newExpanded.has(topic)) {
      newExpanded.delete(topic);
    } else {
      newExpanded.add(topic);
    }
    setExpandedTopics(newExpanded);
  };
  
  // Get tasks for a specific topic
  const getTasksForTopic = (topic: string) => {
    return tasks.filter(t => t.topicPath === topic);
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Target className="w-7 h-7 text-primary-600" />
            Focus
          </h1>
          <p className="text-gray-600 mt-1">
            Tasks you're actively working on right now
          </p>
        </div>
        
        {/* Focus count badge */}
        <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
          isOverLimit 
            ? 'bg-amber-100 text-amber-800' 
            : 'bg-primary-100 text-primary-800'
        }`}>
          <Target className="w-5 h-5" />
          <span className="text-lg font-semibold">{focusCount}</span>
          <span className="text-sm">of {focusLimit}</span>
        </div>
      </div>
      
      {/* Soft warning if over limit */}
      {isOverLimit && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  You have {focusCount} tasks in focus
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  Consider limiting to {focusLimit} for better clarity. 
                  Too many focus tasks can dilute your attention.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Empty state */}
      {!isLoading && focusCount === 0 && (
        <Card className="mb-6">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <Target className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No tasks in focus
            </h3>
            <p className="text-gray-500 mb-4">
              Mark tasks as focused from the Tasks page to track what you're actively working on.
            </p>
            <Button
              variant="secondary"
              onClick={() => window.location.href = '/tasks'}
              className="inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Go to Tasks
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Focus Topics */}
      {sortedTopics.length > 0 && (
        <Card className="mb-6">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2">
              <FolderTree className="w-4 h-4" />
              Focus Topics
            </h3>
            <div className="flex flex-wrap gap-2">
              {sortedTopics.map(({ topic, count }) => (
                <button
                  key={topic}
                  onClick={() => toggleTopic(topic)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ${
                    expandedTopics.has(topic)
                      ? 'bg-primary-100 text-primary-800 border-2 border-primary-300'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
                  }`}
                >
                  <span>{topic}</span>
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white text-xs font-medium">
                    {count}
                  </span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Task list - grouped by topic if topics expanded, otherwise flat */}
      {expandedTopics.size > 0 ? (
        // Show tasks grouped by selected topics
        <div className="space-y-4">
          {sortedTopics
            .filter(({ topic }) => expandedTopics.has(topic))
            .map(({ topic, count }) => {
              const topicTasks = getTasksForTopic(topic);
              return (
                <Card key={topic}>
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <h4 className="font-medium text-gray-900 flex items-center gap-2">
                      <FolderTree className="w-4 h-4 text-gray-500" />
                      {topic}
                      <span className="text-sm text-gray-500">({count})</span>
                    </h4>
                  </div>
                  <CardContent className="p-0">
                    <TaskList
                      tasks={topicTasks}
                      isLoading={false}
                      onEdit={handleEditTask}
                      onComplete={handleCompleteTask}
                      onReopen={handleReopenTask}
                      onFocusToggle={handleUnfocus}
                      emptyMessage="No focused tasks in this topic"
                    />
                  </CardContent>
                </Card>
              );
            })}
        </div>
      ) : (
        // Show all focused tasks in a flat list
        <Card>
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h4 className="font-medium text-gray-900 flex items-center gap-2">
              <Target className="w-4 h-4 text-primary-600" />
              Focused Tasks
              <span className="text-sm text-gray-500">({focusCount})</span>
            </h4>
          </div>
          <CardContent className="p-0">
            <TaskList
              tasks={tasks}
              isLoading={isLoading}
              onEdit={handleEditTask}
              onComplete={handleCompleteTask}
              onReopen={handleReopenTask}
              onFocusToggle={handleUnfocus}
              emptyMessage="No tasks in focus"
            />
          </CardContent>
        </Card>
      )}
      
      {/* Success message when at or under limit */}
      {!isLoading && focusCount > 0 && !isOverLimit && (
        <div className="mt-6 flex items-center justify-center gap-2 text-sm text-green-700">
          <CheckCircle2 className="w-4 h-4" />
          <span>Good focus! You have a manageable number of tasks.</span>
        </div>
      )}

      {/* Edit Task Modal */}
      {editingTask && (
        <TaskEditModal
          task={editingTask}
          isOpen={!!editingTask}
          onClose={() => setEditingTask(null)}
          onSave={handleSaveTask}
          isSaving={updateTask.isPending}
          showPriorityActions={true}
          onComplete={(taskId) => {
            handleCompleteTask(taskId);
            setEditingTask(null);
          }}
          onDismiss={(taskId) => {
            handleDismissTask(taskId);
            setEditingTask(null);
          }}
          onConvertToReference={(taskId) => {
            handleConvertToReference(taskId);
            setEditingTask(null);
          }}
        />
      )}
    </div>
  );
}

